import React, { useEffect, useRef, useState } from 'react';
import { FaCheck, FaPhoneAlt } from 'react-icons/fa';
import Classes from './QuoteForm.module.css';
import { SERVICES } from '../../../data/services';
import { SITE } from '../../../data/siteConfig';
import { formatPhone, isValidPhone, prefetchToken, sendContact } from '../../../utils/contactForm';

const EMPTY = { name: '', phone: '', email: '', service: '', zip: '', message: '', company: '' };

// Short lead form for the hero; sent by api/contact.js.
export default function QuoteForm() {
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState('idle'); // idle | sending | sent | error
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [errorText, setErrorText] = useState('');
    const phoneRef = useRef(null);

    useEffect(() => { prefetchToken(); }, []);

    const phoneError = phoneTouched && !isValidPhone(form.phone);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: name === 'phone' ? formatPhone(value) : value });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!isValidPhone(form.phone)) {
            setPhoneTouched(true);
            phoneRef.current?.focus();
            return;
        }
        setStatus('sending');
        try {
            await sendContact({
                ...form,
                message: form.message.trim() || `Estimate request${form.service ? ` for ${form.service}` : ''}.`,
            });
            setStatus('sent');
            setForm(EMPTY);
            setPhoneTouched(false);
        } catch (err) {
            setErrorText(err.message);
            setStatus('error');
        }
    };

    if (status === 'sent') {
        return (
            <div className={`${Classes.card} ${Classes.sent}`} role="status">
                <span className={Classes.check} aria-hidden="true"><FaCheck /></span>
                <h2 className={Classes.sentTitle}>Request received!</h2>
                <p className={Classes.sentText}>
                    Thanks for reaching out. A member of our team will contact you within 24–48 hours to talk through your project and schedule your free estimate.
                </p>
                <div className={Classes.sentButtons}>
                    <a href={`tel:${SITE.phoneE164}`} className="btn btn-light"><FaPhoneAlt /> Call {SITE.phone}</a>
                    <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-light">Book Online</a>
                </div>
                <button type="button" className={Classes.again} onClick={() => setStatus('idle')}>
                    Send another request
                </button>
            </div>
        );
    }

    return (
        <form className={Classes.card} onSubmit={onSubmit}>
            <h2 className={Classes.title}>Get a Free Estimate</h2>
            <p className={Classes.sub}>Tell us about your project. We usually respond within 24–48 hours.</p>

            <div className={Classes.row}>
                <input name="name" placeholder="Full name" autoComplete="name" maxLength={100} value={form.name} onChange={onChange} required />
                <input
                    ref={phoneRef}
                    name="phone"
                    type="tel"
                    placeholder="Phone"
                    autoComplete="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={onChange}
                    onBlur={() => form.phone && setPhoneTouched(true)}
                    aria-invalid={phoneError}
                    className={phoneError ? Classes.invalid : ''}
                    required
                />
            </div>
            <input name="email" type="email" placeholder="Email" autoComplete="email" maxLength={200} value={form.email} onChange={onChange} required />
            <div className={Classes.row}>
                <select name="service" value={form.service} onChange={onChange} aria-label="Service needed">
                    <option value="">Service needed</option>
                    {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
                    <option value="Other">Other</option>
                </select>
                <input name="zip" placeholder="Zip code" inputMode="numeric" autoComplete="postal-code" maxLength={10} value={form.zip} onChange={onChange} />
            </div>
            <textarea name="message" rows={3} placeholder="Briefly describe the job" maxLength={5000} value={form.message} onChange={onChange} />
            {/* Hidden from people; bots that fill it in are ignored by api/contact.js */}
            <input name="company" value={form.company} onChange={onChange} tabIndex={-1} autoComplete="off" aria-hidden="true" className={Classes.honeypot} />

            {phoneError && <p className={Classes.error}>Please enter a valid 10-digit phone number.</p>}
            <button type="submit" className="btn btn-light" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Request My Free Estimate'}
            </button>
            {status === 'error' && (
                <p className={Classes.error} role="alert">
                    Something went wrong{errorText ? ` (${errorText})` : ''}. Please call {SITE.phone}.
                </p>
            )}
            <p className={Classes.fine}>Free estimates on renovation projects. No obligation.</p>
        </form>
    );
}
